import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { FaDownload, FaPrint, FaSave, FaArrowLeft } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';
import PalletModel3D from '../components/PalletModel3D';
import { parseFrac } from '../utils/parseFrac';
import { api } from '../api';

/* ── Valores por defecto ── */
const DIMS = ['largo', 'ancho', 'alto'];

const defaultNivel    = () => ({ largo: '', ancho: '', alto: '', qty: '' });
const defaultUnits    = () => ({ largo: 'in', ancho: 'in', alto: 'in' });
const defaultDetalle  = () => [];          // array de { ancho:'', largo:'', unit:'in' } por tabla

const DEFAULT = {
  cliente: '',
  fecha: new Date().toISOString().slice(0, 10),
  cargaEst: '',
  cargaDin: '',
  acabado: '',
  superior: { ...defaultNivel(), separacion: '' },
  medio:    defaultNivel(),
  tacon:    { ...defaultNivel(), qty: '9' },
  inferior: defaultNivel(),
};

const DEFAULT_UNITS = {
  superior: { ...defaultUnits(), separacion: 'in' },
  medio:    defaultUnits(),
  tacon:    defaultUnits(),
  inferior: defaultUnits(),
};

const DEFAULT_TACON_SEPS = { superior: [] };

const defaultTaconItem = () => ({ largo: '', ancho: '', alto: '', unit: 'in' });
const DEFAULT_TACON_DIM = Array.from({ length: 9 }, defaultTaconItem);

/* tablasDet: { superior:[{ancho,unit},...], medio:[], tacon:[], inferior:[] }
   Si ancho === '' la tabla hereda el ancho del nivel.                         */
const DEFAULT_DET = {
  superior: defaultDetalle(),
  medio:    defaultDetalle(),
  tacon:    defaultDetalle(),
  inferior: defaultDetalle(),
};

const NIVELES = [
  { key: 'superior', label: 'Tabla Superior', qtyLabel: 'No. de tablas' },
  { key: 'medio',    label: 'Tabla Medio',    qtyLabel: 'No. de tablas' },
  { key: 'tacon',    label: 'Tacón',          qtyLabel: 'No. de tacones' },
  { key: 'inferior', label: 'Tabla Inferior', qtyLabel: 'No. de tablas' },
];

/* ── Polín / Rifle ── */
const defaultPolinNivel = () => ({ largo: '', ancho: '', alto: '', qty: '' });
const defaultPolinUnits = () => ({ largo: 'in', ancho: 'in', alto: 'in' });

const DEFAULT_POLIN = {
  superior: defaultPolinNivel(),
  larguero: { ...defaultPolinNivel(), qty: '4', resaque: '', corte: '', separacion: '' },
  inferior: defaultPolinNivel(),
};
const DEFAULT_POLIN_UNITS = {
  superior: defaultPolinUnits(),
  larguero: { ...defaultPolinUnits(), resaque: 'in', corte: 'in', separacion: 'in' },
  inferior: defaultPolinUnits(),
};
const DEFAULT_POLIN_DET = {
  superior: defaultDetalle(),
  larguero: defaultDetalle(),
  inferior: defaultDetalle(),
};
const DEFAULT_POLIN_SEPS = { larguero: [] };

function syncSeps(prev, newQty) {
  const q = Math.max(0, (parseInt(newQty) || 0) - 1);
  if (prev.length === q) return prev;
  if (prev.length < q) {
    const extra = Array.from({ length: q - prev.length }, () => ({ sep: '', unit: 'in' }));
    return [...prev, ...extra];
  }
  return prev.slice(0, q);
}

const NIVELES_POLIN = [
  { key: 'superior', label: 'Tabla Superior',     qtyLabel: 'No. de tablas',   hint: 'Corren a lo ancho del pallet' },
  { key: 'larguero', label: 'Larguero / Patín',   qtyLabel: 'No. de largueros', hint: 'Corren a lo largo del pallet. Tienen un canal (hueco) en la parte inferior para entrada de montacargas.' },
  { key: 'inferior', label: 'Tabla Inferior',     qtyLabel: 'No. de tablas',   hint: 'Igual que las superiores, van planas en la parte de abajo' },
];

/* Sincroniza el array de detalle al nuevo qty */
function syncDet(prev, newQty) {
  const q = Math.max(0, parseInt(newQty) || 0);
  if (prev.length === q) return prev;
  if (prev.length < q) {
    const extra = Array.from({ length: q - prev.length }, () => ({ ancho: '', largo: '', unit: 'in' }));
    return [...prev, ...extra];
  }
  return prev.slice(0, q);
}

/* Componente inline: input + toggle cm/in */
function DimInput({ value, unit, onValue, onUnit, placeholder = 'ej. 3 1/2' }) {
  const invalid = value !== '' && parseFrac(value) === 0;
  return (
    <DimWrap>
      <DimTextInput
        value={value}
        onChange={e => onValue(e.target.value)}
        placeholder={placeholder}
        $invalid={invalid}
        title='Acepta decimales (3.5) o fracciones (3 1/2 · 5/8)'
      />
      <DimUnitToggle>
        <DimUnitBtn $active={unit === 'cm'} onClick={() => onUnit('cm')}>cm</DimUnitBtn>
        <DimUnitBtn $active={unit === 'in'} onClick={() => onUnit('in')}>in</DimUnitBtn>
      </DimUnitToggle>
    </DimWrap>
  );
}

/* ── Diagrama 2D de planta por nivel ── */
const NIVEL_COLORS = {
  superior: '#D4A455', medio: '#C8973A', inferior: '#B8864A', tacon: '#7A5228',
};

function NivelDiagram2D({ nivelKey, det, defaultAncho, defaultUnit, largo, largoUnit }) {
  const toNum = (val, unit) => {
    const n = parseFrac(val);
    if (!n) return 0;
    return unit === 'in' ? n * 2.54 : n; // siempre en cm internamente
  };

  // Formatea un valor en cm a la unidad preferida para mostrar
  const fmtUnit = (cm, preferUnit) => {
    if (preferUnit === 'in') {
      const inches = cm / 2.54;
      // intentar fracción común
      const whole = Math.floor(inches);
      const frac  = inches - whole;
      const fracs = { 0.125:'⅛', 0.25:'¼', 0.375:'⅜', 0.5:'½', 0.625:'⅝', 0.75:'¾', 0.875:'⅞' };
      const closest = Object.keys(fracs).reduce((a, b) =>
        Math.abs(b - frac) < Math.abs(a - frac) ? b : a, 0);
      if (Math.abs(closest - frac) < 0.04) {
        if (+closest === 0) return `${whole}"`;
        if (+closest === 1) return `${whole + 1}"`;
        return `${whole > 0 ? whole + ' ' : ''}${fracs[closest]}"`;
      }
      return `${inches.toFixed(2)}"`;
    }
    return `${cm % 1 === 0 ? cm : cm.toFixed(1)} cm`;
  };

  const largoN   = toNum(largo, largoUnit) || 120;
  const defaultA = toNum(defaultAncho, defaultUnit) || 9;
  const color    = NIVEL_COLORS[nivelKey] || '#C8973A';

  const boards = det.map(t => ({
    anchoCm:  toNum(t.ancho || '', t.unit || defaultUnit) || defaultA,
    unit:     t.ancho !== '' ? (t.unit || defaultUnit) : defaultUnit,
    rawVal:   t.ancho !== '' ? t.ancho : defaultAncho,
    isCustom: t.ancho !== '',
  }));

  const totalAncho = boards.reduce((s, b) => s + b.anchoCm, 0);

  const SVG_W  = 280;
  const PAD    = 28;
  const DRAW_W = SVG_W - 2 * PAD;
  const GAP    = 3; // px entre tablas (espacio visible)

  // Superiores / Inferiores: corren en X (largo), apiladas en Z (ancho) → vista de planta horizontal
  // Medias: corren en Z (largo vertical), distribuidas en X (ancho) → vista de planta vertical
  const isHoriz = nivelKey === 'superior' || nivelKey === 'inferior';
  const n = boards.length;

  if (isHoriz) {
    const scale     = Math.min(3.5, (DRAW_W * 0.55) / totalAncho);
    const totalGaps = GAP * (n - 1);
    const DRAW_H    = totalAncho * scale + totalGaps;
    const SVG_H     = DRAW_H + 2 * PAD + 18;

    let y = PAD;
    const rects = boards.map((b, i) => {
      const h = b.anchoCm * scale;
      const r = { x: PAD, y, w: DRAW_W, h, b, i };
      y += h + GAP;
      return r;
    });

    return (
      <svg width={SVG_W} height={SVG_H} style={{ display: 'block', margin: '0.5rem auto 0', overflow: 'visible' }}>
        {rects.map(r => (
          <g key={r.i}>
            <rect x={r.x} y={r.y} width={r.w} height={r.h}
              fill={r.b.isCustom ? color : color + 'aa'}
              stroke={r.b.isCustom ? '#1F5D8C' : '#999'}
              strokeWidth={r.b.isCustom ? 1.8 : 0.8}
              rx={1}
            />
            <text x={r.x + 5} y={r.y + r.h / 2}
              fontSize={Math.min(10, Math.max(7, r.h * 0.45))}
              fill="#555" fontFamily="Inter,sans-serif" dominantBaseline="middle">
              #{r.i + 1}
            </text>
            <text x={r.x + r.w - 4} y={r.y + r.h / 2}
              fontSize={Math.min(10, Math.max(7, r.h * 0.45))}
              fill={r.b.isCustom ? '#1F5D8C' : '#444'}
              fontWeight={r.b.isCustom ? '700' : '400'}
              fontFamily="Inter,sans-serif"
              textAnchor="end" dominantBaseline="middle">
              {fmtUnit(r.b.anchoCm, r.b.unit)}
            </text>
          </g>
        ))}
        <line x1={PAD} y1={PAD + DRAW_H + 7} x2={PAD + DRAW_W} y2={PAD + DRAW_H + 7}
          stroke="#D0021B" strokeWidth={1} />
        <line x1={PAD} y1={PAD + DRAW_H + 4} x2={PAD} y2={PAD + DRAW_H + 10}
          stroke="#D0021B" strokeWidth={1} />
        <line x1={PAD + DRAW_W} y1={PAD + DRAW_H + 4} x2={PAD + DRAW_W} y2={PAD + DRAW_H + 10}
          stroke="#D0021B" strokeWidth={1} />
        <text x={PAD + DRAW_W / 2} y={PAD + DRAW_H + 18}
          textAnchor="middle" fontSize={9} fill="#D0021B" fontFamily="Inter,sans-serif">
          Largo: {largo || '—'} {largoUnit}
        </text>
      </svg>
    );
  } else {
    const scale     = Math.min(3.5, (DRAW_W * 0.8) / totalAncho);
    const totalGaps = GAP * (n - 1);
    const DRAW_H    = Math.min(90, largoN * scale * 0.4);
    const realW     = totalAncho * scale + totalGaps;
    const offX      = PAD + (DRAW_W - realW) / 2;
    const SVG_H     = DRAW_H + 2 * PAD + 18;

    let x = offX;
    const rects = boards.map((b, i) => {
      const w = b.anchoCm * scale;
      const r = { x, y: PAD, w, h: DRAW_H, b, i };
      x += w + GAP;
      return r;
    });

    return (
      <svg width={SVG_W} height={SVG_H} style={{ display: 'block', margin: '0.5rem auto 0', overflow: 'visible' }}>
        {rects.map(r => (
          <g key={r.i}>
            <rect x={r.x} y={r.y} width={r.w} height={r.h}
              fill={r.b.isCustom ? color : color + 'aa'}
              stroke={r.b.isCustom ? '#1F5D8C' : '#999'}
              strokeWidth={r.b.isCustom ? 1.8 : 0.8}
              rx={1}
            />
            {r.w >= 14 && (
              <text x={r.x + r.w / 2} y={r.y + r.h / 2}
                textAnchor="middle" dominantBaseline="middle"
                fontSize={Math.min(10, Math.max(7, r.w * 0.35))}
                fill={r.b.isCustom ? '#1F5D8C' : '#444'}
                fontWeight={r.b.isCustom ? '700' : '400'}
                fontFamily="Inter,sans-serif"
                transform={r.w < 28 ? `rotate(-90,${r.x + r.w / 2},${r.y + r.h / 2})` : ''}>
                {fmtUnit(r.b.anchoCm, r.b.unit)}
              </text>
            )}
          </g>
        ))}
        <line x1={offX + realW + 7} y1={PAD} x2={offX + realW + 7} y2={PAD + DRAW_H}
          stroke="#D0021B" strokeWidth={1} />
        <line x1={offX + realW + 4} y1={PAD} x2={offX + realW + 10} y2={PAD}
          stroke="#D0021B" strokeWidth={1} />
        <line x1={offX + realW + 4} y1={PAD + DRAW_H} x2={offX + realW + 10} y2={PAD + DRAW_H}
          stroke="#D0021B" strokeWidth={1} />
        <text x={offX + realW + 13} y={PAD + DRAW_H / 2}
          dominantBaseline="middle" fontSize={9} fill="#D0021B" fontFamily="Inter,sans-serif">
          {largo || '—'} {largoUnit}
        </text>
        <text x={offX + realW / 2} y={PAD + DRAW_H + 14}
          textAnchor="middle" fontSize={9} fill="#888" fontFamily="Inter,sans-serif">
          {boards.length} tablas · {fmtUnit(totalAncho, defaultUnit)} total
        </text>
      </svg>
    );
  }
}

/* ── Menú de tablas individuales ── */
function TablaDetalleMenu({ nivelKey, nivelLabel, det, defaultAncho, defaultUnit, largo, largoUnit, onChange }) {
  const [open, setOpen] = useState(false);
  if (!det || det.length === 0) return null;

  const isInferior = nivelKey === 'inferior';
  const hasCustom  = det.some(t => t.ancho !== '' || (isInferior && t.largo !== ''));

  // Para inferior: 0 = orilla delantera, 1..n-2 = interiores, n-1 = orilla trasera
  const boardLabel = (i) => {
    if (!isInferior) return `#${i + 1}`;
    if (i === 0) return 'Orilla ①';
    if (i === det.length - 1) return 'Orilla ②';
    return `#${i} Interior`;
  };

  return (
    <DetWrap>
      <DetToggle onClick={() => setOpen(o => !o)}>
        <DetToggleLabel>
          {hasCustom && <DetDot title="Tiene medidas personalizadas" />}
          Detalle por tabla ({det.length})
        </DetToggleLabel>
        <span>{open ? '▲' : '▼'}</span>
      </DetToggle>

      {open && (
        <DetGrid>
          <NivelDiagram2D
            nivelKey={nivelKey}
            det={det}
            defaultAncho={defaultAncho}
            defaultUnit={defaultUnit}
            largo={largo}
            largoUnit={largoUnit}
          />
          <DetDivider />
          {det.map((t, i) => {
            const isInterior = isInferior && i > 0 && i < det.length - 1;
            return (
              <DetRow key={i}>
                <DetNum>{boardLabel(i)}</DetNum>
                <DetLabel>Ancho</DetLabel>
                <DimInput
                  value={t.ancho}
                  unit={t.unit}
                  onValue={val => onChange(i, 'ancho', val)}
                  onUnit={val => onChange(i, 'unit', val)}
                  placeholder={defaultAncho || 'hereda'}
                />
                {isInterior && <>
                  <DetLabel>Largo</DetLabel>
                  <DimInput
                    value={t.largo || ''}
                    unit={t.unit}
                    onValue={val => onChange(i, 'largo', val)}
                    onUnit={val => onChange(i, 'unit', val)}
                    placeholder={largo || 'hereda'}
                  />
                </>}
                {(t.ancho !== '' || (isInterior && t.largo !== '')) && (
                  <DetClear onClick={() => { onChange(i, 'ancho', ''); if (isInterior) onChange(i, 'largo', ''); }} title="Restaurar">↺</DetClear>
                )}
              </DetRow>
            );
          })}
          <DetHint>
            {isInferior
              ? 'Orillas corren a lo largo (eje X). Interiores corren al ancho (eje Z) — usa "Largo" para cambiar cuánto abarcan.'
              : `Campo vacío usa el ancho del nivel (${defaultAncho || '—'} ${defaultUnit}).`
            }
          </DetHint>
        </DetGrid>
      )}
    </DetWrap>
  );
}

/* ── Fórmula: pies tablares de una sección ──────────────────────
   PT = Σ (L × Ai × H / 144)  por cada tablón (cantidad Q).
   Si hay anchos individuales en det[], se usa cada uno;
   si no, todos heredan el ancho del nivel.                        */
function toInches(val, unit) {
  const n = parseFrac(val);
  if (!n) return 0;
  return unit === 'cm' ? n / 2.54 : n;
}

function sectionBF(nivel, uNivel, det = []) {
  const L = toInches(nivel.largo, uNivel.largo);
  const H = toInches(nivel.alto,  uNivel.alto);
  const Q = parseInt(nivel.qty) || 0;
  if (L === 0 || H === 0 || Q === 0) return 0;

  const defaultA = toInches(nivel.ancho, uNivel.ancho);

  let total = 0;
  for (let i = 0; i < Q; i++) {
    const t = det[i];
    const A = (t && t.ancho !== '')
      ? toInches(t.ancho, t.unit || uNivel.ancho)
      : defaultA;
    total += (L * A * H) / 144;
  }
  return total;
}

/* ── Defaults precios ── */
const DEFAULT_PRECIO = {
  activo: false,
  precioPT: '',          // precio por pie tablar ($)
  adicionales: [{ desc: '', monto: '' }],
};

export default function Fichas() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const [searchParams] = useSearchParams();

  const [nombre, setNombre]   = useState('');
  const [tipo, setTipo]       = useState('tacon');
  const [form, setForm]       = useState(DEFAULT);
  const [units, setUnits]     = useState(DEFAULT_UNITS);
  const [precio, setPrecio]   = useState(DEFAULT_PRECIO);
  const [tablasDet, setTablasDet] = useState(DEFAULT_DET);
  // Polín / Rifle state
  const [polinNiveles, setPolinNiveles] = useState(DEFAULT_POLIN);
  const [polinUnits,   setPolinUnits]   = useState(DEFAULT_POLIN_UNITS);
  const [polinDet,     setPolinDet]     = useState(DEFAULT_POLIN_DET);
  const [polinSeps,    setPolinSeps]    = useState(DEFAULT_POLIN_SEPS);
  const [taconSeps,    setTaconSeps]    = useState(DEFAULT_TACON_SEPS);
  const [taconDimDet,  setTaconDimDet]  = useState(DEFAULT_TACON_DIM);

  const [snapshot, setSnapshot]   = useState(null);
  const [printTrigger, setPrintTrigger] = useState(0);
  const [fichaId, setFichaId] = useState(null);
  const [saving, setSaving]   = useState(false);
  const palletRef = useRef(null);

  // Cargar ficha existente desde la API si hay :id en la URL
  useEffect(() => {
    if (!id) return;
    api.getFicha(id).then(data => {
      if (!data) return;
      setFichaId(data.id);
      setNombre(data.nombre || '');
      setTipo(data.tipo || 'tacon');
      setForm(data.form      || DEFAULT);
      setUnits(data.units    || DEFAULT_UNITS);
      setPrecio(data.precio  || DEFAULT_PRECIO);
      setTablasDet(data.tablasDet || DEFAULT_DET);
      setPolinNiveles(data.polinNiveles || DEFAULT_POLIN);
      setPolinUnits(data.polinUnits     || DEFAULT_POLIN_UNITS);
      setPolinDet(data.polinDet         || DEFAULT_POLIN_DET);
      setPolinSeps(data.polinSeps       || DEFAULT_POLIN_SEPS);
      setTaconSeps(data.taconSeps       || DEFAULT_TACON_SEPS);
      setTaconDimDet(data.taconDimDet  || DEFAULT_TACON_DIM);
      setSnapshot(data.snapshot   || null);
    }).catch(() => {});
  }, [id]);

  // Auto-imprimir si viene ?print=1
  useEffect(() => {
    if (searchParams.get('print') === '1' && fichaId) {
      setTimeout(() => setPrintTrigger(n => n + 1), 600);
    }
  }, [fichaId]); // eslint-disable-line

  const setTop = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const setNivel = (key, field, val) => {
    setForm(f => ({ ...f, [key]: { ...f[key], [field]: val } }));
    if (field === 'qty') {
      setTablasDet(d => ({ ...d, [key]: syncDet(d[key], val) }));
      if (key === 'superior') {
        setTaconSeps(s => ({ ...s, superior: syncSeps(s.superior, val) }));
      }
    }
  };
  const setUnit = (key, dim, val) =>
    setUnits(u => ({ ...u, [key]: { ...u[key], [dim]: val } }));

  const setTablaField = (nivelKey, i, field, val) =>
    setTablasDet(d => {
      const arr = [...d[nivelKey]];
      arr[i] = { ...arr[i], [field]: val };
      return { ...d, [nivelKey]: arr };
    });

  // Polín helpers
  const setPolinNivel = (key, field, val) => {
    setPolinNiveles(n => ({ ...n, [key]: { ...n[key], [field]: val } }));
    if (field === 'qty') {
      setPolinDet(d => ({ ...d, [key]: syncDet(d[key], val) }));
      if (key === 'larguero') {
        setPolinSeps(s => ({ ...s, larguero: syncSeps(s.larguero, val) }));
      }
    }
  };
  const setPolinUnit = (key, dim, val) =>
    setPolinUnits(u => ({ ...u, [key]: { ...u[key], [dim]: val } }));
  const setPolinDetField = (nivelKey, i, field, val) =>
    setPolinDet(d => {
      const arr = [...d[nivelKey]];
      arr[i] = { ...arr[i], [field]: val };
      return { ...d, [nivelKey]: arr };
    });
  const setPolinSepField = (i, field, val) =>
    setPolinSeps(s => {
      const arr = [...s.larguero];
      arr[i] = { ...arr[i], [field]: val };
      return { ...s, larguero: arr };
    });

  const setTaconSepField = (i, field, val) =>
    setTaconSeps(s => {
      const arr = [...s.superior];
      arr[i] = { ...arr[i], [field]: val };
      return { ...s, superior: arr };
    });

  const setTaconDimField = (i, field, val) =>
    setTaconDimDet(prev => {
      const arr = [...prev];
      if (field === '__clear') {
        arr[i] = defaultTaconItem();
      } else {
        arr[i] = { ...arr[i], [field]: val };
      }
      return arr;
    });

  const setPrecioField = (field, val) => setPrecio(p => ({ ...p, [field]: val }));
  const setAdicional = (i, field, val) =>
    setPrecio(p => {
      const ads = [...p.adicionales];
      ads[i] = { ...ads[i], [field]: val };
      return { ...p, adicionales: ads };
    });
  const addAdicional  = () =>
    setPrecio(p => ({ ...p, adicionales: [...p.adicionales, { desc: '', monto: '' }] }));
  const removeAdicional = i =>
    setPrecio(p => ({ ...p, adicionales: p.adicionales.filter((_, j) => j !== i) }));

  const bf = {
    superior: sectionBF(form.superior, units.superior, tablasDet.superior),
    medio:    sectionBF(form.medio,    units.medio,    tablasDet.medio),
    tacon:    sectionBF(form.tacon,    units.tacon,    tablasDet.tacon),
    inferior: sectionBF(form.inferior, units.inferior, tablasDet.inferior),
  };
  const totalBF  = bf.superior + bf.medio + bf.tacon + bf.inferior;
  const ppt      = parseFloat(precio.precioPT) || 0;
  const subtotal = totalBF * ppt;
  const sumAds   = precio.adicionales.reduce((s, a) => s + (parseFloat(a.monto) || 0), 0);
  const total    = subtotal + sumAds;

  React.useEffect(() => {
    if (printTrigger > 0) window.print();
  }, [printTrigger]);

  const handlePrint = () => {
    const url = palletRef.current?.getSnapshot();
    if (url) setSnapshot(url);
    setPrintTrigger(n => n + 1);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const url   = palletRef.current?.getSnapshot();
    const snap  = url || snapshot;
    const newId = fichaId || `ficha_${Date.now()}`;
    try {
      await api.saveFicha({
        id: newId, nombre, tipo,
        form, units, tablasDet, taconSeps, taconDimDet,
        polinNiveles, polinUnits, polinDet, polinSeps,
        snapshot: snap,
      });
      navigate('/fichas');
    } catch (err) {
      alert('Error al guardar: ' + err.message);
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFichaId(null);
    setNombre('');
    setTipo('tacon');
    setForm(DEFAULT);
    setUnits(DEFAULT_UNITS);
    setPrecio(DEFAULT_PRECIO);
    setTablasDet(DEFAULT_DET);
    setPolinNiveles(DEFAULT_POLIN);
    setPolinUnits(DEFAULT_POLIN_UNITS);
    setPolinDet(DEFAULT_POLIN_DET);
    setPolinSeps(DEFAULT_POLIN_SEPS);
    setTaconSeps(DEFAULT_TACON_SEPS);
    setTaconDimDet(DEFAULT_TACON_DIM);
    setSnapshot(null);
  };

  const dimLabel = { largo: 'Largo', ancho: 'Ancho', alto: 'Alto' };

  return (
    <>
      <PageStyle />
      <Page>
        {/* ── Encabezado ── */}
        <Header className="no-print">
          <Logo src="/img/logo/grupojaycom_logo.png" alt="Grupo Jaycom" />
          <HeaderTitle>Generador de Fichas Técnicas</HeaderTitle>
          <HeaderActions>
            <ActionBtn onClick={() => navigate('/fichas')}>
              <FaArrowLeft size={13} /> Mis fichas
            </ActionBtn>
            <ActionBtn onClick={handleReset}>Limpiar</ActionBtn>
            <ActionBtn $save onClick={handleSave} disabled={saving}>
              <FaSave size={13} /> {saving ? 'Guardando…' : 'Guardar'}
            </ActionBtn>
            <ActionBtn $primary onClick={handlePrint}>
              <FaPrint size={14} /> Imprimir / Guardar PDF
            </ActionBtn>
          </HeaderActions>
        </Header>

        {/* ── Formulario (pantalla) ── */}
        <FormWrap className="no-print">
          <Card>
            <CardTitle>Datos generales</CardTitle>
            <FormGrid>
              <Field style={{ gridColumn: '1 / -1' }}>
                <Label>Tipo de tarima</Label>
                <TypeToggle>
                  <TypeBtn $active={tipo === 'tacon'} onClick={() => setTipo('tacon')}>Tarima de Tacón</TypeBtn>
                  <TypeBtn $active={tipo === 'polin'} onClick={() => setTipo('polin')}>Polín / Rifle</TypeBtn>
                </TypeToggle>
              </Field>
              <Field style={{ gridColumn: '1 / -1' }}>
                <Label>Nombre de la ficha <Optional>(referencia interna)</Optional></Label>
                <Input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Tarima 48×40 — Lote #12" />
              </Field>
              <Field>
                <Label>Cliente</Label>
                <Input value={form.cliente} onChange={e => setTop('cliente', e.target.value)} placeholder="Nombre del cliente" />
              </Field>
              <Field>
                <Label>Fecha</Label>
                <Input type="date" value={form.fecha} onChange={e => setTop('fecha', e.target.value)} />
              </Field>
              <Field>
                <Label>Carga estática (kg)</Label>
                <Input value={form.cargaEst} onChange={e => setTop('cargaEst', e.target.value)} placeholder="Ej: 2000" />
              </Field>
              <Field>
                <Label>Carga dinámica (kg)</Label>
                <Input value={form.cargaDin} onChange={e => setTop('cargaDin', e.target.value)} placeholder="Ej: 1000" />
              </Field>
              <Field style={{ gridColumn: '1 / -1' }}>
                <Label>Acabado <Optional>(opcional)</Optional></Label>
                <Input value={form.acabado} onChange={e => setTop('acabado', e.target.value)} placeholder="Ej: Cepillada, sin astillas" />
              </Field>
            </FormGrid>
          </Card>

          {tipo === 'tacon' && (
          <Card>
            <CardTitle>Tarima de Tacón — Niveles</CardTitle>
            {NIVELES.map(({ key, label, qtyLabel }) => (
              <NivelBlock key={key}>
                <NivelLabel>{label}</NivelLabel>
                <NivelGrid>
                  {DIMS.map(dim => (
                    <Field key={dim}>
                      <Label>{dimLabel[dim]}</Label>
                      <DimInput
                        value={form[key][dim]}
                        unit={units[key][dim]}
                        onValue={val => setNivel(key, dim, val)}
                        onUnit={val => setUnit(key, dim, val)}
                      />
                    </Field>
                  ))}
                  {key === 'superior' && (
                    <Field>
                      <Label>Separación entre tablas <Optional>(libre)</Optional></Label>
                      <DimInput
                        value={form.superior.separacion}
                        unit={units.superior.separacion}
                        onValue={val => setNivel('superior', 'separacion', val)}
                        onUnit={val => setUnit('superior', 'separacion', val)}
                        placeholder="Espacio libre"
                      />
                    </Field>
                  )}
                  <Field>
                    <Label>{qtyLabel}</Label>
                    <Input value={form[key].qty} onChange={e => setNivel(key, 'qty', e.target.value)} placeholder="Cant." />
                  </Field>
                </NivelGrid>
                <TablaDetalleMenu
                  nivelKey={key}
                  nivelLabel={label}
                  det={tablasDet[key]}
                  defaultAncho={form[key].ancho}
                  defaultUnit={units[key].ancho}
                  largo={form[key].largo}
                  largoUnit={units[key].largo}
                  onChange={(i, field, val) => setTablaField(key, i, field, val)}
                />
                {key === 'superior' && taconSeps.superior.length > 0 && (
                  <SepDetalleMenu
                    seps={taconSeps.superior}
                    defaultSep={form.superior.separacion}
                    defaultUnit={units.superior.separacion}
                    onChange={setTaconSepField}
                  />
                )}
                {key === 'tacon' && (
                  <TaconDetalleMenu
                    taconDimDet={taconDimDet}
                    defaultLargo={form.tacon.largo}
                    defaultAncho={form.tacon.ancho}
                    defaultAlto={form.tacon.alto}
                    defaultUnit={units.tacon.largo}
                    onChange={setTaconDimField}
                  />
                )}
              </NivelBlock>
            ))}
          </Card>
          )}

          {tipo === 'polin' && (
          <Card>
            <CardTitle>Polín / Rifle — Niveles</CardTitle>
            {NIVELES_POLIN.map(({ key, label, qtyLabel, hint }) => (
              <NivelBlock key={key}>
                <NivelLabel>{label}</NivelLabel>
                {hint && <NivelHint>{hint}</NivelHint>}
                <NivelGrid>
                  {DIMS.map(dim => (
                    <Field key={dim}>
                      <Label>{dimLabel[dim]}</Label>
                      <DimInput
                        value={polinNiveles[key][dim]}
                        unit={polinUnits[key][dim]}
                        onValue={val => setPolinNivel(key, dim, val)}
                        onUnit={val => setPolinUnit(key, dim, val)}
                      />
                    </Field>
                  ))}
                  {key === 'larguero' && <>
                    <Field>
                      <Label>Resaque (alto del corte)</Label>
                      <DimInput
                        value={polinNiveles.larguero.resaque}
                        unit={polinUnits.larguero.resaque}
                        onValue={val => setPolinNivel('larguero', 'resaque', val)}
                        onUnit={val => setPolinUnit('larguero', 'resaque', val)}
                        placeholder="Alto del hueco"
                      />
                    </Field>
                    <Field>
                      <Label>Corte (ancho de cada corte)</Label>
                      <DimInput
                        value={polinNiveles.larguero.corte}
                        unit={polinUnits.larguero.corte}
                        onValue={val => setPolinNivel('larguero', 'corte', val)}
                        onUnit={val => setPolinUnit('larguero', 'corte', val)}
                        placeholder="Ancho de cada corte"
                      />
                    </Field>
                    <Field>
                      <Label>Separación entre largueros <Optional>(libre)</Optional></Label>
                      <DimInput
                        value={polinNiveles.larguero.separacion}
                        unit={polinUnits.larguero.separacion}
                        onValue={val => setPolinNivel('larguero', 'separacion', val)}
                        onUnit={val => setPolinUnit('larguero', 'separacion', val)}
                        placeholder="Espacio libre"
                      />
                    </Field>
                  </>}
                  <Field>
                    <Label>{qtyLabel}</Label>
                    <Input
                      value={polinNiveles[key].qty}
                      onChange={e => setPolinNivel(key, 'qty', e.target.value)}
                      placeholder="Cant."
                    />
                  </Field>
                </NivelGrid>
                <TablaDetalleMenu
                  nivelKey={key}
                  nivelLabel={label}
                  det={polinDet[key]}
                  defaultAncho={polinNiveles[key].ancho}
                  defaultUnit={polinUnits[key].ancho}
                  largo={polinNiveles[key].largo}
                  largoUnit={polinUnits[key].largo}
                  onChange={(i, field, val) => setPolinDetField(key, i, field, val)}
                />
                {key === 'larguero' && polinSeps.larguero.length > 0 && (
                  <SepDetalleMenu
                    seps={polinSeps.larguero}
                    defaultSep={polinNiveles.larguero.separacion}
                    defaultUnit={polinUnits.larguero.separacion}
                    onChange={setPolinSepField}
                  />
                )}
              </NivelBlock>
            ))}
          </Card>
          )}
        </FormWrap>

        {/* ── Sección de precios (oculta temporalmente) ── */}
        {false && <PrecioWrap className="no-print">
          <PrecioToggleBar onClick={() => setPrecioField('activo', !precio.activo)}>
            <span>💰 Cotización / Precios</span>
            <PrecioChevron $open={precio.activo}>{precio.activo ? '▲' : '▼'}</PrecioChevron>
          </PrecioToggleBar>

          {precio.activo && (
            <PrecioBody>
              {/* ── Tabla pies tablares ── */}
              <BFTable>
                <thead>
                  <tr>
                    <BFTh>Componente</BFTh>
                    <BFTh $right>Pies tablares</BFTh>
                  </tr>
                </thead>
                <tbody>
                  {NIVELES.map(({ key, label }) => (
                    <tr key={key}>
                      <BFTd>{label}</BFTd>
                      <BFTd $right $mono>{bf[key].toFixed(4)} PT</BFTd>
                    </tr>
                  ))}
                  <tr>
                    <BFTd $bold>Total madera</BFTd>
                    <BFTd $right $bold $mono>{totalBF.toFixed(4)} PT</BFTd>
                  </tr>
                </tbody>
              </BFTable>

              {/* ── Precio por pie tablar ── */}
              <PrecioRow>
                <PrecioLabel>Precio por pie tablar (PT)</PrecioLabel>
                <PrecioInputWrap>
                  <PrecioPrefix>$</PrecioPrefix>
                  <PrecioInput
                    value={precio.precioPT}
                    onChange={e => setPrecioField('precioPT', e.target.value)}
                    placeholder="0.00"
                    type="number"
                    min="0"
                    step="0.01"
                  />
                </PrecioInputWrap>
              </PrecioRow>

              <PrecioSubtotal>
                <span>Subtotal madera</span>
                <span>${subtotal.toFixed(2)}</span>
              </PrecioSubtotal>

              {/* ── Adicionales ── */}
              <AdicionalesTitle>Adicionales</AdicionalesTitle>
              {precio.adicionales.map((ad, i) => (
                <AdicionalRow key={i}>
                  <AdicionalDesc
                    value={ad.desc}
                    onChange={e => setAdicional(i, 'desc', e.target.value)}
                    placeholder="Descripción (ej. flete, tratamiento…)"
                  />
                  <PrecioInputWrap>
                    <PrecioPrefix>$</PrecioPrefix>
                    <PrecioInput
                      value={ad.monto}
                      onChange={e => setAdicional(i, 'monto', e.target.value)}
                      placeholder="0.00"
                      type="number"
                      min="0"
                      step="0.01"
                    />
                  </PrecioInputWrap>
                  <RemoveBtn onClick={() => removeAdicional(i)} title="Eliminar">✕</RemoveBtn>
                </AdicionalRow>
              ))}
              <AddAdicionalBtn onClick={addAdicional}>+ Agregar adicional</AddAdicionalBtn>

              <PrecioTotal>
                <span>TOTAL</span>
                <span>${total.toFixed(2)}</span>
              </PrecioTotal>
            </PrecioBody>
          )}
        </PrecioWrap>}

        {/* ── Vista 3D (no se imprime) ── */}
        <ModelSection className="no-print">
          <PalletModel3D
            ref={palletRef}
            tipo={tipo}
            dims={tipo === 'polin' ? polinNiveles : form}
            units={tipo === 'polin' ? polinUnits : units}
            tablasDet={tipo === 'polin' ? polinDet : tablasDet}
            seps={tipo === 'polin' ? polinSeps : taconSeps}
            taconDimDet={tipo === 'tacon' ? taconDimDet : null}
            onSnapshot={setSnapshot}
          />
        </ModelSection>

        {/* ── Ficha imprimible ── */}
        <PrintSheet>
          <PrintHeader>
            <PrintLogo src="/img/logo/grupojaycom_logo.png" alt="Grupo Jaycom" />
            <PrintHeaderRight>
              <PrintTitle>FICHA TÉCNICA</PrintTitle>
              <PrintSubtitle>{tipo === 'polin' ? 'Polín / Rifle' : 'Tarima de Tacón'}</PrintSubtitle>
            </PrintHeaderRight>
            {fichaId && (
              <PrintQRWrap>
                <QRCodeSVG
                  value={`${window.location.origin}/fichas/ver/${fichaId}`}
                  size={80}
                  bgColor="#ffffff"
                  fgColor="#1F5D8C"
                  level="M"
                />
                <PrintQRLabel>Ver ficha digital</PrintQRLabel>
              </PrintQRWrap>
            )}
          </PrintHeader>

          {/* Imagen 3D: visible en pantalla y en print cuando hay snapshot */}
          {snapshot && (
            <PrintModelWrap>
              <img
                src={snapshot}
                alt="Vista 3D de la tarima"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
              <PrintModelCaption>Vista 3D — {tipo === 'polin' ? 'Polín / Rifle' : 'Tarima de Tacón'}</PrintModelCaption>
            </PrintModelWrap>
          )}

          {nombre && <PrintNombre>{nombre}</PrintNombre>}
          <PrintMeta>
            <MetaRow><MetaKey>Cliente:</MetaKey><MetaVal>{form.cliente || '—'}</MetaVal></MetaRow>
            <MetaRow><MetaKey>Fecha:</MetaKey><MetaVal>{form.fecha}</MetaVal></MetaRow>
            <MetaRow><MetaKey>Carga estática:</MetaKey><MetaVal>{form.cargaEst ? `${form.cargaEst} kg` : '—'}</MetaVal></MetaRow>
            <MetaRow><MetaKey>Carga dinámica:</MetaKey><MetaVal>{form.cargaDin ? `${form.cargaDin} kg` : '—'}</MetaVal></MetaRow>
            {form.acabado && <MetaRow><MetaKey>Acabado:</MetaKey><MetaVal>{form.acabado}</MetaVal></MetaRow>}
          </PrintMeta>

          <PrintTableScroll>
            <PrintTable>
              <thead>
                <tr>
                  <Th>Componente</Th>
                  <Th>Largo</Th>
                  <Th>Ancho</Th>
                  <Th>Alto</Th>
                  <Th>Cantidad</Th>
                </tr>
              </thead>
            <tbody>
              {(tipo === 'polin' ? NIVELES_POLIN : NIVELES).map(({ key, label, qtyLabel }) => {
                const nivelData  = tipo === 'polin' ? polinNiveles[key] : form[key];
                const nivelUnits = tipo === 'polin' ? polinUnits[key]   : units[key];
                const det        = tipo === 'polin' ? (polinDet[key] || []) : (tablasDet[key] || []);
                const hasCustom  = det.some(t => t.ancho !== '');
                return (
                  <React.Fragment key={key}>
                    <tr>
                      <Td $bold data-label="Componente">{label}</Td>
                      {DIMS.map(dim => (
                        <Td key={dim} data-label={dimLabel[dim]}>
                          {nivelData[dim] ? `${nivelData[dim]} ${nivelUnits[dim]}` : '—'}
                        </Td>
                      ))}
                      <Td data-label="Cantidad">{nivelData.qty ? `${nivelData.qty} ${qtyLabel.replace('No. de ', '')}` : '—'}</Td>
                    </tr>
                    {hasCustom && det.map((t, i) => (
                      <tr key={`${key}-det-${i}`} style={{ background: '#f7fbff' }}>
                        <Td data-label="Componente" style={{ paddingLeft: '1.5rem', color: '#1F5D8C', fontSize: '0.78rem' }}>
                          ↳ Tabla #{i + 1}
                        </Td>
                        <Td data-label="Largo" style={{ color: '#555', fontSize: '0.78rem' }}>—</Td>
                        <Td data-label="Ancho" style={{ color: t.ancho ? '#1F5D8C' : '#999', fontWeight: t.ancho ? 700 : 400, fontSize: '0.78rem' }}>
                          {t.ancho ? `${t.ancho} ${t.unit}` : `${nivelData.ancho || '—'} ${nivelUnits.ancho}`}
                          {!t.ancho && <span style={{ color: '#aaa', fontStyle: 'italic' }}> (hereda)</span>}
                        </Td>
                        <Td data-label="Alto" style={{ color: '#555', fontSize: '0.78rem' }}>—</Td>
                        <Td data-label="Cantidad" style={{ color: '#555', fontSize: '0.78rem' }}>1</Td>
                      </tr>
                    ))}
                    {tipo === 'polin' && key === 'larguero' && polinNiveles.larguero.resaque && (
                      <tr style={{ background: '#fff8f0' }}>
                        <Td data-label="Componente" style={{ paddingLeft: '1.5rem', color: '#7A5228', fontSize: '0.78rem' }}>
                          ↳ Alto del corte (resaque)
                        </Td>
                        <Td data-label="Largo" style={{ color: '#999', fontSize: '0.78rem' }}>—</Td>
                        <Td data-label="Ancho" style={{ color: '#999', fontSize: '0.78rem' }}>—</Td>
                        <Td data-label="Alto" style={{ color: '#7A5228', fontWeight: 700, fontSize: '0.78rem' }}>
                          {polinNiveles.larguero.resaque} {polinUnits.larguero.resaque}
                        </Td>
                        <Td data-label="Cantidad" style={{ color: '#999', fontSize: '0.78rem' }}>2 cortes</Td>
                      </tr>
                    )}
                    {tipo === 'polin' && key === 'larguero' && polinNiveles.larguero.corte && (
                      <tr style={{ background: '#fff8f0' }}>
                        <Td data-label="Componente" style={{ paddingLeft: '1.5rem', color: '#7A5228', fontSize: '0.78rem' }}>
                          ↳ Ancho de cada corte
                        </Td>
                        <Td data-label="Largo" style={{ color: '#999', fontSize: '0.78rem' }}>—</Td>
                        <Td data-label="Ancho" style={{ color: '#7A5228', fontWeight: 700, fontSize: '0.78rem' }}>
                          {polinNiveles.larguero.corte} {polinUnits.larguero.corte}
                        </Td>
                        <Td data-label="Alto" style={{ color: '#999', fontSize: '0.78rem' }}>—</Td>
                        <Td data-label="Cantidad" style={{ color: '#999', fontSize: '0.78rem' }}>—</Td>
                      </tr>
                    )}
                    {tipo === 'polin' && key === 'larguero' && polinNiveles.larguero.separacion && (
                      <tr style={{ background: '#fff8f0' }}>
                        <Td data-label="Componente" style={{ paddingLeft: '1.5rem', color: '#7A5228', fontSize: '0.78rem' }}>
                          ↳ Separación entre largueros
                        </Td>
                        <Td data-label="Largo" style={{ color: '#999', fontSize: '0.78rem' }}>—</Td>
                        <Td data-label="Ancho" style={{ color: '#7A5228', fontWeight: 700, fontSize: '0.78rem' }}>
                          {polinNiveles.larguero.separacion} {polinUnits.larguero.separacion}
                        </Td>
                        <Td data-label="Alto" style={{ color: '#999', fontSize: '0.78rem' }}>—</Td>
                        <Td data-label="Cantidad" style={{ color: '#999', fontSize: '0.78rem' }}>—</Td>
                      </tr>
                    )}
                    {tipo === 'polin' && key === 'larguero' && polinSeps.larguero.some(s => s.sep !== '') &&
                      polinSeps.larguero.map((s, i) => s.sep !== '' && (
                        <tr key={`sep-${i}`} style={{ background: '#f7fbff' }}>
                          <Td data-label="Componente" style={{ paddingLeft: '2rem', color: '#1F5D8C', fontSize: '0.78rem' }}>
                            ↳ Sep. larguero #{i+1}–#{i+2}
                          </Td>
                          <Td data-label="Largo" style={{ color: '#555', fontSize: '0.78rem' }}>—</Td>
                          <Td data-label="Ancho" style={{ color: '#1F5D8C', fontWeight: 700, fontSize: '0.78rem' }}>
                            {s.sep} {s.unit}
                          </Td>
                          <Td data-label="Alto" style={{ color: '#555', fontSize: '0.78rem' }}>—</Td>
                          <Td data-label="Cantidad" style={{ color: '#555', fontSize: '0.78rem' }}>1</Td>
                        </tr>
                      ))
                    }
                  </React.Fragment>
                );
              })}
            </tbody>
          </PrintTable>
          </PrintTableScroll>

          <PrintFooter>
            <span>Grupo Jaycom — Fabricación de tarimas de madera</span>
            <span>contacto@grupojaycom.com</span>
          </PrintFooter>

          {/* ── Cotización en la ficha imprimible ── */}
          {precio.activo && (
            <PrintCotizacion>
              <PrintSectionTitle>COTIZACIÓN</PrintSectionTitle>
              <PrintTable>
                <thead>
                  <tr>
                    <Th>Componente</Th>
                    <Th style={{textAlign:'right'}}>Pies tablares</Th>
                  </tr>
                </thead>
                <tbody>
                  {NIVELES.map(({ key, label }) => (
                    <tr key={key}>
                      <Td>{label}</Td>
                      <Td style={{textAlign:'right', fontFamily:'monospace'}}>{bf[key].toFixed(4)} PT</Td>
                    </tr>
                  ))}
                  <tr style={{borderTop:'2px solid #000'}}>
                    <Td $bold>Total madera</Td>
                    <Td $bold style={{textAlign:'right', fontFamily:'monospace'}}>{totalBF.toFixed(4)} PT</Td>
                  </tr>
                </tbody>
              </PrintTable>

              <PrintPrecioGrid>
                {ppt > 0 && (
                  <PrintPrecioRow>
                    <span>Precio PT</span>
                    <span>${ppt.toFixed(2)} / PT</span>
                  </PrintPrecioRow>
                )}
                {ppt > 0 && (
                  <PrintPrecioRow>
                    <span>Subtotal madera</span>
                    <span>${subtotal.toFixed(2)}</span>
                  </PrintPrecioRow>
                )}
                {precio.adicionales.filter(a => a.desc || a.monto).map((ad, i) => (
                  <PrintPrecioRow key={i}>
                    <span>{ad.desc || 'Adicional'}</span>
                    <span>${(parseFloat(ad.monto) || 0).toFixed(2)}</span>
                  </PrintPrecioRow>
                ))}
                <PrintPrecioTotal>
                  <span>TOTAL</span>
                  <span>${total.toFixed(2)}</span>
                </PrintPrecioTotal>
              </PrintPrecioGrid>
            </PrintCotizacion>
          )}
        </PrintSheet>
      </Page>
    </>
  );
}

/* ── Menú de medidas individuales por tacón (9 posiciones en 3×3) ── */
const TACON_LABELS = [
  'Izq · Frente', 'Izq · Centro', 'Izq · Atrás',
  'Cen · Frente', 'Centro',       'Cen · Atrás',
  'Der · Frente', 'Der · Centro', 'Der · Atrás',
];

function TaconDetalleMenu({ taconDimDet, defaultLargo, defaultAncho, defaultAlto, defaultUnit, onChange }) {
  const [open, setOpen] = useState(false);

  const hasCustom = taconDimDet.some(t => t.largo !== '' || t.ancho !== '' || t.alto !== '');

  return (
    <DetWrap>
      <DetToggle onClick={() => setOpen(o => !o)}>
        <DetToggleLabel>
          {hasCustom && <DetDot title="Tiene medidas personalizadas" />}
          Medidas individuales por tacón (9)
        </DetToggleLabel>
        <span>{open ? '▲' : '▼'}</span>
      </DetToggle>

      {open && (
        <DetGrid>
          {taconDimDet.map((t, i) => {
            const dirty = t.largo !== '' || t.ancho !== '' || t.alto !== '';
            return (
              <TaconRow key={i}>
                <TaconRowLabel>
                  {dirty && <DetDot />}
                  #{i + 1} {TACON_LABELS[i]}
                </TaconRowLabel>
                <TaconFields>
                  <DetLabel>Largo</DetLabel>
                  <DimInput value={t.largo} unit={t.unit}
                    onValue={val => onChange(i, 'largo', val)}
                    onUnit={val => onChange(i, 'unit', val)}
                    placeholder={defaultLargo || '—'} />
                  <DetLabel>Ancho</DetLabel>
                  <DimInput value={t.ancho} unit={t.unit}
                    onValue={val => onChange(i, 'ancho', val)}
                    onUnit={val => onChange(i, 'unit', val)}
                    placeholder={defaultAncho || '—'} />
                  <DetLabel>Alto</DetLabel>
                  <DimInput value={t.alto} unit={t.unit}
                    onValue={val => onChange(i, 'alto', val)}
                    onUnit={val => onChange(i, 'unit', val)}
                    placeholder={defaultAlto || '—'} />
                </TaconFields>
                {dirty && (
                  <DetClear onClick={() => onChange(i, '__clear', '')} title="Restaurar medidas del nivel">↺</DetClear>
                )}
              </TaconRow>
            );
          })}
          <DetHint>
            Campo vacío usa la medida del nivel. Largo y Ancho son la huella del tacón; Alto afecta la altura de la tarima.
          </DetHint>
        </DetGrid>
      )}
    </DetWrap>
  );
}

/* ── Menú de separaciones individuales entre largueros ── */
function SepDetalleMenu({ seps, defaultSep, defaultUnit, onChange }) {
  const [open, setOpen] = useState(false);
  if (!seps || seps.length === 0) return null;

  const hasCustom = seps.some(s => s.sep !== '');

  return (
    <DetWrap>
      <DetToggle onClick={() => setOpen(o => !o)}>
        <DetToggleLabel>
          {hasCustom && <DetDot title="Tiene separaciones personalizadas" />}
          Separación individual ({seps.length})
        </DetToggleLabel>
        <span>{open ? '▲' : '▼'}</span>
      </DetToggle>

      {open && (
        <DetGrid>
          {seps.map((s, i) => (
            <DetRow key={i}>
              <DetNum style={{ minWidth: '38px', fontSize: '0.68rem' }}>#{i+1}–{i+2}</DetNum>
              <DetLabel>Sep.</DetLabel>
              <DimInput
                value={s.sep}
                unit={s.unit}
                onValue={val => onChange(i, 'sep', val)}
                onUnit={val => onChange(i, 'unit', val)}
                placeholder={defaultSep || 'hereda'}
              />
              {s.sep !== '' && (
                <DetClear onClick={() => onChange(i, 'sep', '')} title="Usar separación general">↺</DetClear>
              )}
            </DetRow>
          ))}
          <DetHint>
            Espacio libre entre largueros consecutivos. Si dejas vacío, usa la separación general ({defaultSep || '—'} {defaultUnit}).
          </DetHint>
        </DetGrid>
      )}
    </DetWrap>
  );
}

/* ── Print CSS ── */
const PageStyle = createGlobalStyle`
  @page {
    size: A4 portrait;
    margin: 0;
  }
  @media print {
    .no-print { display: none !important; }
    body {
      background: white !important;
      margin: 0 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
  @media screen {
    .print-only { display: none !important; }
  }
`;

/* ── Styled components ── */
const Page = styled.div`
  min-height: 100vh;
  background: var(--mid);
  padding: 2rem;
  font-family: 'Inter', sans-serif;
  @media (max-width: 600px) { padding: 1rem 0.75rem; }
  @media print { min-height: 0; background: white; padding: 0; }
`;

const Header = styled.header`
  max-width: 900px;
  margin: 0 auto 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  @media (max-width: 600px) {
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const Logo = styled.img`
  height: 60px;
  object-fit: contain;
`;

const HeaderTitle = styled.h1`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.8rem;
  color: var(--blue);
  letter-spacing: 0.04em;
  flex: 1;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  @media (max-width: 600px) {
    width: 100%;
    & > button { flex: 1; justify-content: center; padding: 0.6rem 0.5rem; font-size: 0.75rem; }
  }
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.65rem 1.4rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  background: ${({ $primary, $save }) => $primary ? 'var(--blue)' : $save ? '#2e7d32' : 'var(--border)'};
  color: ${({ $primary, $save }) => ($primary || $save) ? '#fff' : 'var(--text)'};
  transition: opacity 0.2s;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const FormWrap = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Card = styled.div`
  background: white;
  padding: 2rem 2rem 1.5rem;
  border-top: 3px solid var(--blue);
  @media (max-width: 600px) { padding: 1.25rem 1rem 1rem; }
`;

const CardTitle = styled.h2`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.3rem;
  color: var(--blue);
  letter-spacing: 0.04em;
  margin-bottom: 1.5rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.5rem;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const NivelBlock = styled.div`
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--border);
  &:last-child { border-bottom: none; margin-bottom: 0; }
`;

const NivelLabel = styled.p`
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--red);
  font-weight: 700;
  margin-bottom: 0.75rem;
`;

const NivelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem 0.75rem;
  @media (max-width: 700px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 400px) { grid-template-columns: minmax(0, 1fr); }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const Label = styled.label`
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-sub);
`;

const Optional = styled.span`
  font-size: 0.65rem;
  color: var(--text-sub);
  text-transform: none;
  letter-spacing: 0;
  margin-left: 0.3rem;
`;

const Input = styled.input`
  border: none;
  border-bottom: 1.5px solid var(--border);
  background: transparent;
  padding: 0.5rem 0;
  font-size: 0.92rem;
  font-family: 'Inter', sans-serif;
  color: var(--text);
  outline: none;
  transition: border-color 0.2s;
  &:focus { border-color: var(--blue); }
  &::placeholder { color: var(--text-sub); opacity: 0.5; }
`;

/* ── Hoja imprimible ── */
const PrintSheet = styled.div`
  max-width: 900px;
  margin: 2rem auto 0;
  background: white;
  padding: 3rem;
  border-top: 4px solid var(--blue);

  @media screen {
    border: 1px solid var(--border);
    border-top: 4px solid var(--blue);
  }
  @media screen and (max-width: 600px) {
    padding: 1.25rem 1rem;
    margin-top: 1rem;
  }
  @media print {
    margin: 0;
    padding: 1.5cm 2cm 2cm;
    max-width: 100%;
    border: none;
    border-top: 4px solid #1F5D8C;
  }
`;

const PrintHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e5e5e5;
  break-inside: avoid;
  page-break-inside: avoid;
  @media (max-width: 500px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1rem;
    padding-bottom: 1rem;
  }
`;

const PrintLogo = styled.img`
  height: 70px;
  object-fit: contain;
`;

const PrintHeaderRight = styled.div`
  text-align: right;
`;

const PrintTitle = styled.h2`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 2rem;
  color: #1F5D8C;
  letter-spacing: 0.06em;
`;

const PrintSubtitle = styled.p`
  font-size: 0.8rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #D0021B;
`;

const PrintNombre = styled.p`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--blue);
  margin-bottom: 0.75rem;
`;

const PrintMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.4rem 2rem;
  margin-bottom: 2rem;
  break-inside: avoid;
  page-break-inside: avoid;
  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    margin-bottom: 1.25rem;
  }
`;

const MetaRow = styled.div`
  display: flex;
  gap: 0.5rem;
  font-size: 0.88rem;
`;

const MetaKey = styled.span`
  color: #555;
  min-width: 130px;
`;

const MetaVal = styled.span`
  font-weight: 600;
  color: #1a1a1a;
`;

const PrintTableScroll = styled.div`
  margin-bottom: 2.5rem;
`;

const PrintTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  thead { display: table-header-group; }
  tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  @media (max-width: 540px) {
    /* Modo tarjeta: ocultar encabezados, cada fila es una tarjeta */
    thead { display: none; }
    tbody, tr, td { display: block; width: 100%; }
    tr {
      margin-bottom: 0.75rem;
      border: 1px solid #e5e5e5;
      border-radius: 4px;
      overflow: hidden;
    }
    td {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 0.85rem;
      border-bottom: 1px solid #f0f0f0;
      font-size: 0.84rem;
      &:last-child { border-bottom: none; }
      &::before {
        content: attr(data-label);
        font-size: 0.68rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #888;
        font-weight: 600;
        flex-shrink: 0;
        margin-right: 0.5rem;
      }
    }
  }
`;

const Th = styled.th`
  background: #1F5D8C;
  color: white;
  padding: 0.7rem 1rem;
  text-align: left;
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #e5e5e5;
  color: #1a1a1a;
  font-weight: ${({ $bold }) => $bold ? '700' : '400'};
  background: ${({ $bold }) => $bold ? '#EBF3FA' : 'transparent'};
`;

const PrintFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid #e5e5e5;
  font-size: 0.75rem;
  color: #888;
  @media (max-width: 500px) {
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const UnitToggleRow = styled.div`display:none`;
const UnitLabel = styled.span`display:none`;
const UnitBtnGroup = styled.div`display:none`;
const UnitBtn = styled.button`display:none`;

/* ── DimInput inline ── */
const DimWrap = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1.5px solid var(--border);
  overflow: hidden;
  flex: 1;
  min-width: 0;
`;

const DimTextInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0.5rem 0.25rem 0.5rem 0;
  font-size: 0.88rem;
  font-family: 'Inter', sans-serif;
  color: ${p => p.$invalid ? '#c0392b' : 'var(--text)'};
  outline: none;
  &::placeholder { color: var(--text-sub); opacity: 0.5; }
`;

const DimUnitToggle = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const DimUnitBtn = styled.button`
  padding: 0.12rem 0.35rem;
  font-size: 0.62rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid var(--border);
  background: ${({ $active }) => $active ? 'var(--blue)' : 'transparent'};
  color: ${({ $active }) => $active ? '#fff' : 'var(--text-sub)'};
  transition: background 0.15s, color 0.15s;
  line-height: 1.4;
  white-space: nowrap;

  &:first-child {
    border-right: none;
    border-radius: 2px 0 0 2px;
  }
  &:last-child {
    border-radius: 0 2px 2px 0;
  }
  &:hover { background: var(--blue); color: #fff; }
`;

const ModelSection = styled.div`
  display: block;
  position: relative;
  width: 100%;
  max-width: 900px;
  margin: 0 auto 2rem;
  overflow: hidden;
  box-shadow: 0 2px 18px rgba(0,0,0,0.10);
`;

/* ── Precio / Cotización ────────────────────────────────────── */
const PrecioWrap = styled.div`
  max-width: 900px;
  margin: 0 auto 2rem;
  background: white;
  border-top: 3px solid var(--blue);
`;

const PrecioToggleBar = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: none;
  border: none;
  cursor: pointer;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.2rem;
  letter-spacing: 0.05em;
  color: var(--blue);
  &:hover { background: var(--blue-lt); }
  @media (max-width: 600px) { padding: 0.85rem 1rem; font-size: 1rem; }
`;

const PrecioChevron = styled.span`
  font-size: 0.85rem;
  color: var(--blue);
`;

const PrecioBody = styled.div`
  padding: 0 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  @media (max-width: 600px) { padding: 0 1rem 1.25rem; }
`;

const BFTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;
const BFTh = styled.th`
  text-align: ${p => p.$right ? 'right' : 'left'};
  padding: 0.4rem 0.5rem;
  border-bottom: 2px solid var(--blue);
  font-family: 'Bebas Neue', sans-serif;
  letter-spacing: 0.04em;
  color: var(--blue);
  font-size: 0.8rem;
`;
const BFTd = styled.td`
  padding: 0.35rem 0.5rem;
  text-align: ${p => p.$right ? 'right' : 'left'};
  font-weight: ${p => p.$bold ? 700 : 400};
  font-family: ${p => p.$mono ? 'monospace' : 'Inter, sans-serif'};
  border-bottom: 1px solid var(--border);
  font-size: 0.85rem;
`;

const PrecioRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;
const PrecioLabel = styled.label`
  flex: 1;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text);
  min-width: 180px;
`;
const PrecioInputWrap = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1.5px solid var(--border);
  gap: 0.2rem;
  min-width: 120px;
`;
const PrecioPrefix = styled.span`
  font-size: 0.85rem;
  color: var(--text-sub);
  padding-bottom: 2px;
`;
const PrecioInput = styled.input`
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.88rem;
  font-family: 'Inter', sans-serif;
  width: 100px;
  padding: 0.35rem 0;
  color: var(--text);
  &::placeholder { color: var(--text-sub); opacity: 0.5; }
`;

const PrecioSubtotal = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.88rem;
  padding: 0.5rem 0;
  border-top: 1px solid var(--border);
  color: var(--text-sub);
`;

const AdicionalesTitle = styled.p`
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--blue);
  margin: 0;
`;

const AdicionalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  @media (max-width: 500px) {
    & > input { min-width: 100%; }
    & > div { flex: 1; }
  }
`;
const AdicionalDesc = styled.input`
  flex: 1;
  min-width: 180px;
  border: none;
  border-bottom: 1.5px solid var(--border);
  background: transparent;
  outline: none;
  font-size: 0.85rem;
  font-family: 'Inter', sans-serif;
  padding: 0.35rem 0;
  color: var(--text);
  &::placeholder { color: var(--text-sub); opacity: 0.5; }
`;
const RemoveBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: #c0392b;
  font-size: 0.8rem;
  padding: 0.2rem 0.4rem;
  &:hover { opacity: 0.7; }
`;
const AddAdicionalBtn = styled.button`
  align-self: flex-start;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.8rem;
  color: var(--blue);
  font-weight: 600;
  padding: 0;
  &:hover { text-decoration: underline; }
`;

const PrecioTotal = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 1rem;
  font-weight: 700;
  padding: 0.75rem 0 0;
  border-top: 2px solid var(--blue);
  color: var(--blue);
  font-family: 'Bebas Neue', sans-serif;
  letter-spacing: 0.05em;
`;

/* ── Cotización en ficha imprimible ── */
const PrintModelWrap = styled.div`
  margin-bottom: 1.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 3px;
  overflow: hidden;
  break-inside: avoid;
  page-break-inside: avoid;
  /* Ocultar en pantalla si el img todavía no tiene src */
  @media screen {
    &:has(img[src=""]) { display: none; }
  }
`;

const PrintModelCaption = styled.p`
  font-size: 0.7rem;
  text-align: center;
  color: #888;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
  padding: 0.35rem 0;
  background: #f9f9f9;
  border-top: 1px solid #e5e5e5;
`;

const PrintCotizacion = styled.div`
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 2px solid #000;
`;
const PrintSectionTitle = styled.h3`
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.2rem;
  letter-spacing: 0.06em;
  color: #1F5D8C;
  margin: 0 0 0.75rem;
`;
const PrintPrecioGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-top: 1rem;
`;
const PrintPrecioRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  padding: 0.2rem 0;
  border-bottom: 1px solid #e5e5e5;
`;
const PrintPrecioTotal = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 1rem;
  font-weight: 700;
  padding: 0.5rem 0 0;
  border-top: 2px solid #000;
  margin-top: 0.3rem;
`;

/* ── Detalle por tabla ─────────────────────────────────────── */
const DetWrap = styled.div`
  margin-top: 0.5rem;
  border-top: 1px dashed var(--border);
`;

const DetToggle = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.45rem 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--blue);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  &:hover { opacity: 0.75; }
`;

const DetToggleLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const DetDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--red);
  flex-shrink: 0;
`;

const DetGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.5rem 0 0.75rem;
`;

const DetRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
`;

const DetNum = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--blue);
  min-width: 22px;
  text-align: right;
  flex-shrink: 0;
`;

const DetLabel = styled.span`
  font-size: 0.72rem;
  color: var(--text-sub);
  flex-shrink: 0;
  min-width: 38px;
`;

const DetClear = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.85rem;
  color: #888;
  padding: 0 0.2rem;
  flex-shrink: 0;
  &:hover { color: var(--red); }
`;

const DetHint = styled.p`
  font-size: 0.68rem;
  color: var(--text-sub);
  margin: 0.25rem 0 0;
  font-style: italic;
`;

const DetDivider = styled.hr`
  border: none;
  border-top: 1px dashed var(--border);
  margin: 0.25rem 0;
`;

const TaconRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.6rem 0;
  border-bottom: 1px dashed var(--border);
  &:last-of-type { border-bottom: none; }
`;

const TaconRowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--blue);
  letter-spacing: 0.04em;
`;

const TaconFields = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.25rem 0.5rem;
  align-items: center;
`;

/* ── Selector de tipo de tarima ── */
const TypeToggle = styled.div`
  display: flex;
  border: 1.5px solid var(--border);
  border-radius: 3px;
  overflow: hidden;
  width: fit-content;
  margin-top: 0.25rem;
`;

const TypeBtn = styled.button`
  padding: 0.55rem 1.4rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  border: none;
  background: ${({ $active }) => $active ? 'var(--blue)' : 'transparent'};
  color: ${({ $active }) => $active ? '#fff' : 'var(--text-sub)'};
  transition: background 0.15s, color 0.15s;
  &:hover { background: var(--blue); color: #fff; }
`;

const NivelHint = styled.p`
  font-size: 0.68rem;
  color: var(--text-sub);
  font-style: italic;
  margin: -0.25rem 0 0.6rem;
`;

const PrintQRWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  margin-left: auto;
  flex-shrink: 0;
`;

const PrintQRLabel = styled.span`
  font-size: 0.6rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #888;
  text-align: center;
`;
